/**
 * useFilteredData Hook
 *
 * Filters comparisons and responses based on enabled models.
 * Returns filtered data for both Comparisons and Responses views.
 * Handles uninitialized filter state gracefully (shows all data when filters not yet synced).
 *
 * Step 2 of ResultsDemo_BabyStep_Plan implementation
 */

import { useMemo } from 'react';
import type { Comparison, Response } from '../../../lib/types/results';

export interface FilteredData {
  comparisons: Comparison[];
  responses: Response[];
  totalComparisons: number;
  totalResponses: number;
  filteredComparisons: number;
  filteredResponses: number;
}

export function useFilteredData(
  comparisons: Comparison[] | undefined,
  responses: Response[] | undefined,
  enabledModelIds: string[],
  comparisonSearchQuery?: string,
  favorites?: Set<string>,
  availableFiltersCount?: number,
  responseSearchQuery?: string
): FilteredData {
  return useMemo(() => {
    const allComparisons = comparisons || [];
    const allResponses = responses || [];

    // If no model filters are set (empty array), show ALL data by default
    // This ensures comparisons are visible even when filters haven't been initialized
    const showAllData = enabledModelIds.length === 0;

    // Filter responses by enabled models (or show all if no filters)
    let filteredResponses = showAllData
      ? allResponses
      : allResponses.filter(response => enabledModelIds.includes(response.modelId));

    // Filter comparisons that have at least one response from enabled models (or show all if no filters)
    let filteredComparisons = showAllData
      ? allComparisons
      : allComparisons.filter(comparison => {
          const comparisonResponses = allResponses.filter(
            r => r.comparisonId === comparison.id
          );
          return comparisonResponses.some(r => enabledModelIds.includes(r.modelId));
        });

    // Apply search filter if query provided (case-insensitive AND logic)
    const normalizedComparisonQuery = comparisonSearchQuery?.trim().toLowerCase() ?? '';
    const normalizedResponseQuery = responseSearchQuery?.trim().toLowerCase()
      || normalizedComparisonQuery;

    if (normalizedComparisonQuery) {
      
      filteredComparisons = filteredComparisons.filter(comparison => {
        // Search in title
        if (comparison.title?.toLowerCase().includes(normalizedComparisonQuery)) {
          return true;
        }

        // Search in description
        if (comparison.description?.toLowerCase().includes(normalizedComparisonQuery)) {
          return true;
        }

        // Search in prompt (if available)
        if ((comparison as any).prompt?.toLowerCase().includes(normalizedComparisonQuery)) {
          return true;
        }

        // Search in model names
        const comparisonResponses = allResponses.filter(
          r => r.comparisonId === comparison.id
        );
        return comparisonResponses.some(r =>
          r.modelId?.toLowerCase().includes(normalizedComparisonQuery)
        );
      });
    }

    if (normalizedResponseQuery) {
      filteredResponses = filteredResponses.filter(response => {
        const combinedText = [
          response.response,
          response.content,
          response.model?.displayName,
          response.modelId,
          response.provider?.name,
          response.providerName,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (combinedText.includes(normalizedResponseQuery)) {
          return true;
        }

        const parentComparison = allComparisons.find(comparison => comparison.id === response.comparisonId);

        if (!parentComparison) {
          return false;
        }

        return [
          parentComparison.title,
          parentComparison.description,
          (parentComparison as any).prompt,
        ]
          .filter(Boolean)
          .some(text => text.toLowerCase().includes(normalizedResponseQuery));
      });
    }

    return {
      comparisons: filteredComparisons,
      responses: filteredResponses,
      totalComparisons: allComparisons.length,
      totalResponses: allResponses.length,
      filteredComparisons: filteredComparisons.length,
      filteredResponses: filteredResponses.length
    };
  }, [
    comparisons,
    responses,
    enabledModelIds,
    comparisonSearchQuery,
    favorites,
    availableFiltersCount,
    responseSearchQuery,
  ]);
}
